<template>
  <div layout="column">
    <div layout="row">
      <div class="title mb-3">
        Daggesrapport
      </div>
    </div>
    <div
      v-if="loaded"
      layout-gt-md="row"
      layout-align="start stretch"
      layout-wrap
      style="width: 100%"
    >
      <div
        v-if="hasNoForm"
        class="alert"
      >
        <v-icon>mdi-alert-circle-outline</v-icon>
        <span>No daggesrapport' form has been found for this type/concept of session. Please contact an administrator.</span>
      </div>

      <v-form
        v-if="!hasNoForm"
        ref="form"
        v-model="formValid"
        @submit.stop.prevent="save"
      >
        <div class="main-container">
          <div class="form-container">
            <generic-form
              v-if="rapport.fields.length"
              :key="`${rapport.id}-${rapport.updated_at}`"
              :evaluation="rapport"
              :readonly="partiallyValidated"
              @change="updateRapport"
            />
            <em v-else>This form has no fields to display</em>
          </div>
          <div class="meta-container">
            <div class="meta">
              <h2>Participants</h2>
              <v-text-field
                v-model="rapport.number_of_participants"
                label="Number of participants"
                outline
                :rules="[v => !!v || 'This field is required']"
                :readonly="partiallyValidated"
              />
              <div
                v-if="!partiallyValidated"
                class="save-section"
              >
                <v-btn
                  depressed
                  color="primary"
                  @click="save"
                >
                  Save form
                </v-btn>
              </div>
              <div
                v-if="hasBeenSavedOnce"
                class="problems"
              >
                <h2>Problems</h2>
                <a
                  v-if="!isProblemEditing"
                  @click="newProblem"
                >Report a problem</a>
                <div
                  v-if="isProblemEditing"
                  class="form-problem"
                >
                  <h3>Report a new problem</h3>
                  <v-textarea
                    v-model="editedProblem.description"
                    label="Description of the problem"
                    rows="7"
                  />
                  <v-text-field
                    v-model="selectedPictures"
                    label="Pictures"
                    prepend-icon="mdi-image"
                    readonly
                    @click="pickPictures()"
                  />
                  <input
                    ref="pictures"
                    type="file"
                    style="display: none"
                    accept="image/*"
                    multiple
                    @change="onPicturesPicked($event)"
                  >
                  <div
                    v-if="pictures.length"
                    layout="row"
                    layout-align="start center"
                    layout-wrap
                    class="mt-4"
                  >
                    <template v-for="(m,i) in pictures">
                      <div
                        :key="'pic_'+i"
                        v-if="picturesUrls[i]"
                        class="mr-2 mb-2"
                        style="position: relative"
                      >
                        <span
                          @click="deletePicture(i)"
                          class="close"
                        >&times;</span>
                        <img
                          :src="picturesUrls[i]"
                          :alt="m.name"
                          :title="m.name"
                          style="max-width: 100px; max-height: 100px"
                        />
                      </div>
                    </template>
                  </div>
                  <v-switch
                    v-model="editedProblem.is_accident"
                    :label="editedProblem.is_accident ? 'An accident occurred' : 'No accident occurred'"
                    :true-value="1"
                    :false-value="0"
                  />
                  <div v-if="editedProblem.is_accident">
                    <div id="report-tools">
                      <v-text-field
                        v-model="editedProblem.report.name"
                        label="Report PDF"
                        prepend-icon="mdi-attachment"
                        @click="pickFile()"
                      />
                      <input
                        ref="reportFile"
                        type="file"
                        style="display: none"
                        accept="application/pdf"
                        @change="onFilePicked($event)"
                      >
                    </div>
                  </div>
                  <div class="sub-actions">
                    <v-btn @click="reportProblem">
                      <span v-if="!editedProblem.id">Report</span><span v-else>Update</span>
                    </v-btn>
                    <span>&nbsp;or&nbsp;&nbsp;</span>
                    <a @click="isProblemEditing = false">Cancel</a>
                  </div>
                </div>
                <div
                  v-if="problems.length"
                  class="problems-list"
                >
                  <div
                    v-for="problem in problems"
                    :key="`${problem.id}-${problem.updated_at}`"
                    class="problem"
                    :class="{edited: isProblemEditing && editedProblem.id == problem.id}"
                  >
                    <div class="flex-container">
                      <div class="problem-header">
                        <div
                          class="icon-container"
                          :class="{accident: problem.is_accident}"
                          :title="problem.is_accident ? 'An accident occurred' : 'No accident occurred'"
                        >
                          <v-icon>mdi-account-injury-outline</v-icon>
                        </div>
                      </div>
                      <div class="problem-description">
                        <div class="nl2br">
                          {{ problem.description }}
                        </div>
                        <div v-if="problem.is_accident">
                          <a @click="downloadReport(problem)">Download report</a>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="problem.documents.length"
                      layout="row"
                      layout-align="start center"
                      layout-wrap
                      class="mt-2"
                    >
                      <div
                        v-for="m in problem.documents"
                        :key="'pic_'+ m.id"
                        class="mr-2 mb-2 clickable"
                        style="position: relative"
                      >
                        <img
                          :src="getImageData(m.id)"
                          style="max-width: 100px; max-height: 100px"
                          @click="$refs['pic-dialog'].show(getImageData(m.id))"
                        />
                      </div>
                    </div>
                    <div class="problem-actions">
                      <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-dots-vertical
                          </v-icon>
                        </template>
                        <v-card>
                          <ul>
                            <li
                              :key="`edit-${problem.id}`"
                              @click="editProblem(problem)"
                            >
                              Edit
                            </li>
                            <li
                              :key="`delete-${problem.id}`"
                              @click="openDeleteModal[problem.id] = true"
                            >
                              Delete
                            </li>
                          </ul>
                        </v-card>
                      </v-menu>
                      <v-menu
                        v-model="openDeleteModal[problem.id]"
                        :attach="true"
                        :close-on-content-click="false"
                        :nudge-width="400"
                        :min-width="400"
                        offset-x
                        offset-y
                        left
                      >
                        <v-card>
                          <v-card-title class="title">
                            Delete the problem ?
                          </v-card-title>
                          <v-card-text>Are you sure you want to delete this problem ?</v-card-text>
                          <v-card-actions
                            layout="row"
                            layout-align="end center"
                          >
                            <v-btn
                              flat
                              @click="openDeleteModal[problem.id] = false"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                              flat
                              color="error"
                              @click="deleteProblem(problem)"
                            >
                              Delete
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="hasBeenSavedOnce"
                class="validations"
              >
                <h2>Validations</h2>
                <div
                  class="legend"
                  :class="{'full-validated': fullValidated}"
                >
                  <strong>Info: </strong>
                  <span v-if="!fullValidated">Trainers won't be able to register their own evaluations until each trainer has validated the Daggesrapport</span>
                  <span v-else>Trainers can now register their own evaluations</span>
                </div>
                <div class="trainers-validations">
                  <template v-for="trainer in session.trainers">
                    <div
                      :key="`${trainer.id}-${validations[trainer.id].id ?? 'null'}`"
                      class="validation"
                    >
                      <div class="validation-header">
                        <h3>{{ trainer.firstname }} {{ trainer.lastname }}</h3>
                        <a
                          v-if="(trainer.id == currentUser.id || currentUser.is_admin || isAppCoord(session)) && validations[trainer.id].id && editedValidation != validations[trainer.id]"
                          title="Edit the validation comment"
                          @click="startEdition(validations[trainer.id])"
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </a>
                      </div>
                      <div>
                        <div v-if="validations[trainer.id].id && editedValidation != validations[trainer.id]">
                          <div class="comment">
                            <div
                              v-if="validations[trainer.id].comment"
                              class="bubble"
                            >
                              <p class="nl2br">
                                {{ validations[trainer.id].comment }}
                              </p>
                            </div>
                            <p v-else>
                              (no comment)
                            </p>
                          </div>
                          <em class="validated">validated <span style="width: 100px">{{ formatDayHuman(validations[trainer.id].created_at) }}</span> at {{ formatTime(validations[trainer.id].created_at) }}</em>
                        </div>
                        <div v-else-if="trainer.id == currentUser.id || currentUser.is_admin || isAppCoord(session)">
                          <v-textarea
                            v-model="validations[trainer.id].comment"
                            label="Comment about this Daggesrapport"
                            @keyup.esc="stopEdition(validations[trainer.id])"
                          />
                          <div class="validation-sub">
                            <v-btn @click="validate(trainer)">
                              <span>Validate</span>
                            </v-btn>
                            <div v-if="validations[trainer.id].id && editedValidation == validations[trainer.id]">
                              <span>&nbsp;ou&nbsp;&nbsp;</span>
                              <a @click="stopEdition(validations[trainer.id])">Cancel</a>
                            </div>
                          </div>
                          <em
                            v-if="trainer.id != currentUser.id"
                            class="administrator-tips"
                          >
                            <strong>Tips: </strong>As administrator or CT, you can validate the Daggesrapport on behalf the trainer
                          </em>
                        </div>
                        <div v-else>
                          Still waiting the validation of this trainer
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-form>
    </div>
    <picture-dialog ref="pic-dialog" />
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import Sessions from '@/api/sessions'
import DateMixin from '@/mixins/date'
import ImageMixin from '@/mixins/image'
import Auth from '@/plugins/auth'
import GenericForm from './GenericForm/Form'
import PictureDialog from "@/components/shared/PictureDialog.vue"
import GlobalMixin from '@/mixins/global'
import FormDataMixin from '@/mixins/formdata'
import Consts from '@/consts'

export default {
  components: {GenericForm, PictureDialog},
  mixins: [DateMixin, GlobalMixin, FormDataMixin, ImageMixin],
  props: ['session'],
  data () {
    return {
      rapport: {},
      validations: {},
      hasNoForm: false,
      user: null,
      loaded: false,
      formValid: false,
      editedValidation: null,
      initialEditedValidationComment: "",
      isProblemEditing: false,
      editedProblem: {
        id: null,
        description: "",
        is_accident: false,
        report_id: null,
        report: {
          name: "",
          data: null
        }
      },
      imgs: {},
      imgsLoad: {},
      problems: [],
      pictures: [],
      picturesUrls: [],
      deletedPictures: [],
      openDeleteModal: {},
      Consts
    }
  },
  computed: {
    hasBeenSavedOnce () {
      return !!this.rapport.saved_once && this.rapport.saved_once != "0" //PDO retourne une string
    },
    currentUser () {
      return Auth.user
    },
    fullValidated() {
      return this.session.trainers.reduce((acc, t) => {
        return acc && this.validations[t.id].id != null
      }, true)
    },
    partiallyValidated() {
      return !!this.session.trainers.find(t => this.validations[t.id].id != null)
    },
    selectedPictures () {
      if(!this.pictures.length){
        return null
      }

      return this.pictures.length + ' picture' + (this.pictures.length > 1 ? 's' : '') + ' selected'
    }
  },
  mounted () {
    this.fetch()
    this.user = Auth.user.id
  },
  methods: {
    pickPictures() {
      this.$refs['pictures'].click();
    },
    onPicturesPicked (e) {
      const files = e.target.files
      for (let i = 0; i < files.length; i++) {
        if (files[i] !== undefined) {
          const f = files[i]
          this.pictures.push(f)
          const u = URL.createObjectURL(f)
          this.picturesUrls.push(u)
        }
      }
      e.target.value = ""
    },
    deletePicture (i) {
      if (this.pictures[i].id) {
        this.deletedPictures.push(this.pictures[i].id)
      }
      this.pictures.splice(i, 1)
      this.picturesUrls.splice(i, 1)
    },
    async fetch () {
      try {
        const {data} = await Sessions.getDaggesrapport(this.$route.params.id)
        this.rapport = data
        const {data: data2} = await Sessions.getDaggesrapportValidations(this.$route.params.id)

        if(this.session.trainers) {
          this.session.trainers.forEach(t => {
            Vue.set(this.validations, t.id, {id: null, comment: "", trainer_id: t.id, evaluation_id: this.rapport.id, created_at: null})
          })
          if(data2) {
            data2.forEach(d => {
              if(this.validations[d.trainer_id]) {
                this.validations[d.trainer_id] = d
              }
            })
          }
        }

        const {data: data3} = await Sessions.getDaggesrapportProblems(this.$route.params.id)
        this.problems = data3
        this.problems.forEach(p => {
          Vue.set(this.openDeleteModal, p.id, false)
          if(p.documents && p.documents.length){
            p.documents.forEach(d => {
              this.getImage(
                "/assets/problemDocuments/" + d.id,
                d.id
              )
            })
          }
        })
        this.loaded = true
      }
      catch(e) {
        if(e.response && e.response.status == 422) {
          this.hasNoForm = true
        }
        this.loaded = true
      }
    },
    updateRapport(evaluation) {
      let nbParticipants = this.rapport.number_of_participants //petit hack pour pas perdre l'éventuelle modif du nb de participants
      this.rapport = evaluation
      this.rapport.number_of_participants = nbParticipants
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$snotify.error('Please check the content of your form')
        return;
      }
      try {
        const {data} = await Sessions.updateDaggesrapport(this.$route.params.id, this.rapport)
        this.rapport = data
        this.$snotify.success('The report has been saved successfully')
      }
      catch(e) {
        //intercepted by axios
      }
    },
    async validate(trainer) {
      try {
        let data = null
        let isEditing = false
        if( ! this.validations[trainer.id].id) { //pas encore sauvegardée
          data = await Sessions.postDaggesrapportValidation(this.$route.params.id, this.validations[trainer.id])
        }
        else {
          isEditing = true
          data = await Sessions.updateDaggesrapportValidation(this.$route.params.id, this.validations[trainer.id].id, this.validations[trainer.id])
        }

        this.validations[trainer.id].id = data.data.id
        this.validations[trainer.id].created_at = data.data.created_at

        if(isEditing) {
          this.editedValidation = null
          this.initialEditedValidationComment = ""
        }
      }
      catch(e) {
        //intercepted by axios
        console.log(e)
      }
    },
    startEdition(validation) {
      this.editedValidation = validation
      this.initialEditedValidationComment = validation.comment
    },
    stopEdition(validation) {
      validation.comment = this.initialEditedValidationComment
      this.editedValidation = null
    },
    resetProblem(p = null) {
      this.editedProblem.id = p ? p.id : null
      this.editedProblem.description  = p ? p.description : ""
      this.editedProblem.is_accident  = p ? p.is_accident : false
      this.editedProblem.report_id    = p ? p.report_id : null
      this.editedProblem.report.name  = ""
      this.editedProblem.report.data  = ""
      this.deletedPictures = []
      this.pictures = []
      this.picturesUrls = []

      if(p){
        for (let i = 0; i < p.documents.length; i++) {
          axios
            .get("/assets/problemDocuments/" + p.documents[i].id)
            .then(res => {
              this.pictures.push(p.documents[i])
              this.picturesUrls.push(res.data)
            })
        }
      }
    },
    newProblem() {
      this.resetProblem()
      this.isProblemEditing = true
    },
    editProblem(p) {
      this.resetProblem(p)
      this.isProblemEditing = true
    },
    async deleteProblem(p) {
      await Sessions.deleteDaggesrapportProblem(this.$route.params.id, p.id);
      this.problems.splice(this.problems.indexOf(this.problems.find(p => p.id == this.editedProblem.id)), 1)
      this.openDeleteModal[p.id] = false
    },
    async reportProblem() {
      //on teste la validité
      if(this.editedProblem.description.trim() == "") {
        this.$snotify.error('The description of the problem is missing')
        return
      }
      if((!this.editedProblem.id ||  ! this.editedProblem.report_id) && this.editedProblem.is_accident && (! this.editedProblem.report.data || ! this.editedProblem.report.name)) {
        this.$snotify.error('The accident report is mandatory')
        return
      }

      let o = Object.assign(
        {},
        this.editedProblem,
        { deleted_docs: this.deletedPictures }
      )

      let formData = new FormData();
      if (this.editedProblem.report.data && this.editedProblem.report.name) {
          formData.append("report", this.editedProblem.report.data, this.editedProblem.report.name);
      }
      this._convertObjectToFormData(formData, o);

      for (let i = 0; i < this.pictures.length; i++) {
        if (!this.pictures[i].id) {
          formData.append(
            "documents[" + i + "]",
            this.pictures[i],
            this.pictures[i].name
          )
        }
      }

      if( ! this.editedProblem.id) { //new
        const {data} = await Sessions.postDaggesrapportProblem(this.$route.params.id, formData)
        data.is_accident = data.is_accident == "true" || data.is_accident == "1" ? 1 : 0
        Vue.set(this.openDeleteModal, data.id, false)
        this.problems.unshift(data)
      }
      else {
        const {data} = await Sessions.updateDaggesrapportProblem(this.$route.params.id, this.editedProblem.id, formData)
        data.is_accident = data.is_accident == "true" || data.is_accident == "1" ? 1 : 0
        this.problems.splice(this.problems.indexOf(this.problems.find(p => p.id == this.editedProblem.id)), 1, data)
      }
      this.isProblemEditing = false
      this.resetProblem()
      this.fetch()
    },
    pickFile() {
      this.$refs['reportFile'].click();
    },
    onFilePicked(e) {
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.editedProblem.report.data = files[0];
        this.editedProblem.report.name = files[0].name;
      }
    },
    downloadReport (problem) {
      let a = document.createElement("a");
      a.style = "display: none";
      document.body.appendChild(a);

      axios.get(`/sessions/${this.$route.params.id}/daggesrapports-problems/${problem.id}/report`, {responseType: 'blob'}).then(res => {
        let blob = new Blob([res.data], {type: "octet/stream"});

        if (window.navigator && window.navigator.msSaveOrOpenBlob){
          window.navigator.msSaveBlob(blob, problem.report.name); //il faudra aller chercher le vrai nom du fichier
        }
        else
        {
          let url = window.URL.createObjectURL(blob);
          a.href = url;
          a.download = problem.report.name
          a.click();
          window.URL.revokeObjectURL(url);
        }
      })
    }
  }
}
</script>

<style scoped lang="sass">
  .close
    position: absolute;
    top: 2px;
    right: 2px;
    z-index: 100;
    color: white;
    text-shadow: red 0.1em 0.1em 0.2em;
    cursor: pointer;
    border-radius: 50% 50%;
    background-color: red;
    width: 14px;
    text-align: center;
    vertical-align: middle;
    line-height: 1;

  .legend
    border-left: 5px solid tomato
    padding-left: 10px
    color: #999999
    margin-top: 10px

    &.full-validated
      border-left: 5px solid #82e0aa

  .administrator-tips
    display: inline-block
    margin-top: 10px
    color: #999999
  .alert
    padding: 2em
    border-radius: 9px
    border: 2px solid tomato
    color: tomato
    width: 100%
    font-size: 1.2rem
    display: flex
    flex-direction: row
    align-items: center

    i.v-icon
      font-size: 60px
      color: tomato
      margin-right: 20px

  form
    width: 100%

  .main-container
    width: 100%
    display: flex
    flex-direction: row
    align-items: top
    flex-wrap: wrap-reverse

    .form-container
      flex-grow: 1
      min-width: 500px
      background-color: white
      padding: 24px
      border-radius: 9px

    .meta-container
      width: 400px
      margin-left: 24px
      position: relative

      .meta
        position: sticky
        top: 0px
        padding-top: 20px


      .save-section
        display: flex
        flex-direction: row
        justify-content: center

  .validation, .problems
    margin: 20px 0px

  .validation-header
    display: flex
    flex-direction: row
    justify-content: space-between
    align-items: center

    i
      width: 30px
      height: 30px
      border-radius: 50%

      &:hover
        background-color: #D1D1D1

  .validation-sub
    display: flex
    flex-direction: row
    align-items: center

  .comment
    margin: 10px 0 4px 0

  .validated
    font-size: 0.9em

  .bubble
    border-radius: 7px
    background-color: #fce48a
    display: inline-block
    padding: 15px 18px
    position: relative
    width: 100%

    &:before
      content: "\00a0"
      display: block
      background-color: #fce48a
      height: 20px
      width: 20px
      position: absolute
      top: -7px
      left: 12px
      -webkit-transform: rotate(315deg) skew(5deg)

    > p
      margin: 0
      padding: 0

  a
    display: inline-block
    text-decoration: underline

    &.disabled
      color: #AAAAAA
      cursor: default

  .sub-actions
    display: flex
    flex-direction: row
    align-items: center


  .problem
    padding: 10px
    border-radius: 8px
    background-color: white
    margin: 10px 0
    position: relative

    &.edited
      background-color: #fef9e7

    .flex-container
      display: flex
      flex-direction: row

      .problem-description
        padding: 0 10px

        .nl2br
          margin-bottom: 5px

      .problem-header
        > .icon-container
          border-radius: 50%
          background-color: #F1F1F1
          width: 30px
          height: 30px
          display: flex
          justify-content: center
          align-items: center

          i
            color: #D1D1D1


          &.accident
            background-color: tomato
            i
              color: white

    .problem-actions
      position: absolute
      top: 5px
      right: 5px
      width: 24px
      height: 24px
      border-radius: 12px
      z-index: 100
      opacity:


      &:hover
        background-color: #F1F1F1

  .v-menu__content, .v-menu
    opacity: 1
    z-index: 100 !important

  .v-menu__content ul
    opacity: 1
    margin: 0
    padding: 0
    list-style: none
    list-style-type: none
    width: 120px

    li
      padding: 10px
      cursor: pointer

      &:hover
        background-color: #F1F1F1




</style>
